import React, { memo, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Prescription, PrescriptionComplaint, PrescriptionMedicine } from "@/types/prescription";
import {
  Calendar,
  Edit,
  Copy,
  Thermometer,
  Heart,
  Activity,
  Gauge,
  Printer,
  Weight,
} from "lucide-react";
import { format, isValid, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

interface VisitCardProps {
  prescription: Prescription;
  onEdit?: (prescription: Prescription) => void;
  onView?: (prescription: Prescription) => void;
  onCopy?: (prescription: Prescription) => void;
  isLatest?: boolean;
  className?: string;
  doctorName?: string;
}

function VisitCard({
  prescription,
  onEdit,
  onView,
  onCopy,
  isLatest = false,
  className,
  doctorName,
}: VisitCardProps) {

  // Doctor name — from prescription data or prop, with "Dr." prefix; null if unavailable
  const displayDoctorName = useMemo(() => {
    const name = (prescription.doctor_name || doctorName || "").trim();
    if (!name) return null;
    const lower = name.toLowerCase();
    if (lower.startsWith("dr.") || lower.startsWith("dr ")) return name;
    return `Dr. ${name}`;
  }, [prescription.doctor_name, doctorName]);

  // Date formatting
  const dateInfo = useMemo(() => {
    if (!prescription?.date) return null;
    try {
      const visitDate = new Date(prescription.date);
      if (!isValid(visitDate)) return null;
      return {
        formatted: format(visitDate, "dd-MMM-yyyy"),
        relative:  formatDistanceToNow(visitDate, { addSuffix: true }),
      };
    } catch {
      return null;
    }
  }, [prescription?.date]);

  // Complaints
  const complaintsInfo = useMemo(() => {
    if (!Array.isArray(prescription.complaints) || prescription.complaints.length === 0) return null;
    const valid = prescription.complaints.filter(
      (c): c is PrescriptionComplaint => Boolean(c?.complaint?.trim())
    );
    return valid.length ? valid : null;
  }, [prescription.complaints]);

  // Medicines
  const medicineInfo = useMemo(() => {
    if (!Array.isArray(prescription.medicines)) return { count: 0, list: [] };
    const valid = prescription.medicines.filter(
      (m): m is PrescriptionMedicine => Boolean(m?.product?.trim())
    );
    return { count: valid.length, list: valid };
  }, [prescription.medicines]);

  // Investigations
  const investigationInfo = useMemo(() => {
    if (!Array.isArray(prescription.investigations)) return { count: 0, list: [] };
    const valid = prescription.investigations.filter(
      (i): i is string => typeof i === "string" && i.trim().length > 0
    );
    return { count: valid.length, list: valid };
  }, [prescription.investigations]);

  // Has enough content to copy
  const hasSubstantialContent = useMemo(() => Boolean(
    prescription.diagnosis?.trim() ||
    medicineInfo.count > 0 ||
    complaintsInfo?.length ||
    prescription.advice?.trim() ||
    investigationInfo.count > 0
  ), [prescription.diagnosis, medicineInfo.count, complaintsInfo, prescription.advice, investigationInfo.count]);

  const formatDosage = (m: PrescriptionMedicine) => m.dosage || "-";
  const formatTiming = (m: PrescriptionMedicine) =>
    m.taken_when ?? (m as { when?: string }).when ?? "-";

  return (
    <Card className={cn(
      "border border-border shadow-sm overflow-hidden bg-card",
      isLatest && "border-l-4 border-l-primary",
      className
    )}>
      <CardContent className="p-0">

        {/* Header */}
        <div className="px-4 py-2 border-b border-border bg-muted/40">
          <div className="flex items-center justify-between gap-3">

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="font-semibold text-foreground text-sm">
                {displayDoctorName ? `${displayDoctorName} · ` : ""}{dateInfo?.formatted ?? "—"}
              </span>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                ({dateInfo?.relative})
              </span>
              {isLatest && (
                <Badge className="bg-primary text-primary-foreground text-xs px-2 py-0.5">
                  Latest
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {onCopy && hasSubstantialContent && (
                <Button variant="outline" size="sm" onClick={() => onCopy(prescription)}
                  className="h-7 px-2 gap-1 text-xs">
                  <Copy className="w-3 h-3" /> Copy Rx
                </Button>
              )}
              {onEdit && (
                <Button variant="outline" size="sm" onClick={() => onEdit(prescription)}
                  className="h-7 px-2 gap-1 text-xs">
                  <Edit className="w-3 h-3" /> Edit
                </Button>
              )}
              <Button variant="default" size="sm" onClick={() => onView?.(prescription)}
                className="h-7 px-2 gap-1 text-xs">
                <Printer className="w-3 h-3" /> Print
              </Button>
            </div>
          </div>
        </div>

        {/* Body — clinical order: Complaints → Vitals → Diagnosis → Rx → Investigations → Advice → Notes → Next Visit */}
        <div className="p-4 space-y-4">

          {/* Complaints */}
          {complaintsInfo && (
            <div>
              <SectionLabel>Chief Complaints</SectionLabel>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {complaintsInfo.map((c, i) => (
                  <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-full text-foreground">
                    {c.complaint}
                    {c.duration && <span className="text-muted-foreground ml-1">· {c.duration}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Vitals — only render if at least one meaningful value exists (exclude empty/"None") */}
          {(() => {
            const v = prescription.vitals;
            if (!v) return null;
            const meaningful = (val?: string | number) => { const s = val != null ? String(val).trim() : ""; return s !== "" && s !== "None"; };
            const hasNumeric  = meaningful(v.bp) || meaningful(v.pulse) || meaningful(v.spo2) || meaningful(v.temp) || meaningful(v.weight);
            const hasClinical = meaningful(v.edema) || meaningful(v.pallor) || meaningful(v.icterus);
            if (!hasNumeric && !hasClinical) return null;
            return (
              <div>
                <SectionLabel>Vitals</SectionLabel>
                <div className="flex flex-wrap gap-4 mt-1">
                  {meaningful(v.bp)     && <VitalItem icon={<Gauge      className="w-3.5 h-3.5 text-primary"           />} label="BP"     value={`${v.bp} mmHg`}  />}
                  {meaningful(v.pulse)  && <VitalItem icon={<Heart      className="w-3.5 h-3.5 text-destructive"       />} label="Pulse"  value={`${v.pulse} bpm`} />}
                  {meaningful(v.spo2)   && <VitalItem icon={<Activity   className="w-3.5 h-3.5 text-success"           />} label="SpO2"   value={`${v.spo2}%`}     />}
                  {meaningful(v.temp)   && <VitalItem icon={<Thermometer className="w-3.5 h-3.5 text-warning"          />} label="Temp"   value={`${v.temp}°F`}    />}
                  {meaningful(v.weight) && <VitalItem icon={<Weight     className="w-3.5 h-3.5 text-muted-foreground"  />} label="Weight" value={`${v.weight} kg`} />}
                  {meaningful(v.edema)   && <VitalItem icon={<Activity  className="w-3.5 h-3.5 text-muted-foreground"  />} label="Edema"   value={v.edema!}   />}
                  {meaningful(v.pallor)  && <VitalItem icon={<Activity  className="w-3.5 h-3.5 text-muted-foreground"  />} label="Pallor"  value={v.pallor!}  />}
                  {meaningful(v.icterus) && <VitalItem icon={<Activity  className="w-3.5 h-3.5 text-muted-foreground"  />} label="Icterus" value={v.icterus!} />}
                </div>
              </div>
            );
          })()}

          {/* Diagnosis */}
          {prescription.diagnosis && (
            <div>
              <SectionLabel>Diagnosis</SectionLabel>
              <p className="text-sm text-foreground mt-1">{prescription.diagnosis}</p>
            </div>
          )}

          {/* Medicines */}
          {medicineInfo.count > 0 && (
            <div>
              <SectionLabel>Rx</SectionLabel>
              <div className="overflow-x-auto mt-1 rounded-md border border-border/60 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60">
                    <tr>
                      {["#", "Medicine", "Dosage", "When", "Duration", "Qty", "Notes"].map((h) => (
                        <th key={h} className={cn(
                          "border-b border-border/40 px-2 py-1.5 font-semibold text-muted-foreground",
                          h === "#" || h === "Qty" ? "text-center w-8" : h === "Medicine" ? "text-left" : "text-center"
                        )}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {medicineInfo.list.map((medicine, index) => (
                      <tr key={medicine.id || `med-${index}`}>
                        <td className="px-2 py-1.5 text-center text-muted-foreground">{index + 1}</td>
                        <td className="px-2 py-1.5 font-semibold text-foreground">
                          {medicine.brand_name || medicine.product}
                          {medicine.generic_name && (
                            <div className="text-[10px] text-muted-foreground font-normal italic">{medicine.generic_name}</div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center font-mono">{formatDosage(medicine)}</td>
                        <td className="px-2 py-1.5 text-center">{formatTiming(medicine)}</td>
                        <td className="px-2 py-1.5 text-center">{medicine.duration || "-"}</td>
                        <td className="px-2 py-1.5 text-center">{medicine.quantity || "-"}</td>
                        <td className="px-2 py-1.5 text-center text-muted-foreground">{medicine.notes || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Investigations */}
          {investigationInfo.count > 0 && (
            <div>
              <SectionLabel>Tests Requested</SectionLabel>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {investigationInfo.list.map((test, i) => (
                  <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-full text-foreground">{test}</span>
                ))}
              </div>
            </div>
          )}

          {/* Advice */}
          {prescription.advice && (
            <div>
              <SectionLabel>Advice</SectionLabel>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{prescription.advice}</p>
            </div>
          )}

          {/* Quick Notes */}
          {prescription.quick_notes && (
            <div>
              <SectionLabel>Notes</SectionLabel>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{prescription.quick_notes}</p>
            </div>
          )}

          {/* Next Visit */}
          {prescription.next_visit?.date && (
            <div>
              <SectionLabel>Next Visit</SectionLabel>
              <div className="flex items-center gap-2 mt-1 text-sm text-foreground">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span>{format(new Date(prescription.next_visit.date), "dd-MMM-yyyy")}</span>
                {prescription.next_visit.notes && (
                  <span className="text-muted-foreground">· {prescription.next_visit.notes}</span>
                )}
              </div>
            </div>
          )}

        </div>
      </CardContent>
    </Card>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
      {children}
    </h3>
  );
}

function VitalItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-muted-foreground text-xs">{label}:</span>
      <span className="font-semibold text-foreground text-xs">{value}</span>
    </div>
  );
}

export default memo(VisitCard);
